import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  dispatchKeepInTouchSuggestion,
  draftKeepInTouchSuggestion,
  getKeepInTouchConfig,
  getKeepInTouchSuggestions,
  type KeepInTouchSuggestionDto,
  serverErrorMessage,
  skipKeepInTouchSuggestion,
  snoozeKeepInTouchSuggestion,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';
import { useWorkSummary } from '../../lib/use-work-summary.js';
import { SnoozePopover, snoozeUntilIso } from '../triage/SnoozePopover.js';
import { AiEvidenceStrip } from '../ui/AiEvidenceStrip.js';
import { QueuePage, QueueScoreboard, type QueueTab } from './QueuePage.js';

/**
 * R18 — Keep in touch ported onto QueuePage (was `KeepInTouchModal`, deleted). The
 * proactive check-in queue as a routed `?view=keep-in-touch`: suggestions in the
 * list (trigger chips filter them), the AI draft + gated Send in the canvas,
 * time-based snooze (migration 0045) + skip, Done-today scoreboard.
 */

const TRIGGERS = [
  { key: 'cadence', label: 'Cadence' },
  { key: 'dormant', label: 'Dormant' },
  { key: 'news', label: 'News' },
  { key: 'birthday', label: 'Birthday' },
] as const;

function isFutureSnooze(item: KeepInTouchSuggestionDto): boolean {
  return item.snooze_until != null && new Date(item.snooze_until).getTime() > Date.now();
}

// Upper bound on the "Drafting…" indicator (F4): the fallback that releases it if a
// redraft regenerates identical text (no signature change to clear it). Comfortably
// exceeds the worker draft time + the 30s list poll that surfaces a changed draft.
const DRAFTING_MAX_MS = 45_000;

export function KeepInTouchView() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>('queue');
  const [trigger, setTrigger] = useState<string | null>(null);

  const feed = useQuery({
    queryKey: ['keep-in-touch'],
    queryFn: ({ signal }) => getKeepInTouchSuggestions({ signal }),
    // Poll fast while a draft is still being produced on the worker (no SSE here).
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((i) => i.status === 'pending' || i.status === 'drafted')
        ? 3_000
        : LIVE_POLL_MS,
  });
  const cfg = useQuery({
    queryKey: ['keep-in-touch-config'],
    queryFn: ({ signal }) => getKeepInTouchConfig({ signal }),
  });
  const work = useWorkSummary();
  const sendArmed = Boolean(cfg.data?.send_enabled);

  const allItems = feed.data?.items ?? [];
  const items = useMemo(
    () => (trigger ? allItems.filter((i) => i.trigger === trigger) : allItems),
    [allItems, trigger],
  );

  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  return (
    <QueuePage<KeepInTouchSuggestionDto>
      testId="keep-in-touch-view"
      title="Keep in touch"
      count={items.length}
      items={items}
      getId={(i) => i.id}
      selectedId={selectedId}
      onSelect={setSelectedId}
      rowEstimate={60}
      isLoading={feed.isLoading}
      isError={feed.isError}
      onRetry={() => void feed.refetch()}
      emptyTitle={trigger ? 'Nothing for this trigger' : 'No check-ins due'}
      emptyHint="Enrol contacts (tier / cadence / birthday) in the address book and they appear here when due."
      tab={tab}
      onTabChange={setTab}
      capabilityBadge={
        <span data-testid="kit-capability">
          {sendArmed ? 'review · approve · send' : 'review only'}
        </span>
      }
      filterChips={
        <>
          <TriggerChip label="All" active={trigger === null} onClick={() => setTrigger(null)} />
          {TRIGGERS.map((t) => (
            <TriggerChip
              key={t.key}
              label={t.label}
              active={trigger === t.key}
              onClick={() => setTrigger(t.key)}
            />
          ))}
        </>
      }
      scoreboard={
        <QueueScoreboard
          openCount={allItems.length}
          doneToday={work.data?.done_today?.keep_in_touch ?? 0}
        />
      }
      renderRow={(item) => <KitRowSummary item={item} />}
      renderCanvas={(item) =>
        item ? (
          <KitCanvas key={item.id} item={item} sendArmed={sendArmed} />
        ) : (
          <p className="queue-canvas-empty">Select a check-in to review its draft.</p>
        )
      }
    />
  );
}

function TriggerChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? 'queue-chip active' : 'queue-chip'}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function KitRowSummary({ item }: { item: KeepInTouchSuggestionDto }) {
  const snoozed = isFutureSnooze(item);
  const label =
    item.status === 'sent'
      ? 'Sent'
      : snoozed
        ? 'Snoozed'
        : item.status === 'ready'
          ? 'Draft ready'
          : item.status === 'suppressed'
            ? 'AI declined'
            : item.status === 'failed'
              ? 'Draft failed'
              : 'Needs draft';
  return (
    <span className="jc-row">
      <span className="jc-row-headline">{item.contact_name ?? 'A contact'}</span>
      <span className="jc-row-meta">
        <span className="jc-kind">{item.trigger}</span>
        <span
          className={`jc-dot jc-dot-${snoozed ? 'muted' : item.status === 'ready' ? 'ready' : item.status === 'sent' ? 'ok' : 'pending'}`}
        >
          {label}
        </span>
        {item.due_at ? <time>{formatRelative(item.due_at)}</time> : null}
      </span>
    </span>
  );
}

function KitCanvas({ item, sendArmed }: { item: KeepInTouchSuggestionDto; sendArmed: boolean }) {
  const qc = useQueryClient();
  const readyText = item.status === 'ready' ? (item.draft_text ?? '') : '';
  const [text, setText] = useState(readyText);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(item.status === 'sent');
  const [drafting, setDrafting] = useState(false);
  const lastText = useRef<string | null>(item.draft_text ?? null);
  // (status,text) signature the row waits to change away from, so Redraft shows
  // "Drafting…" until the NEW draft lands (KIT has no draft id → key on status+text;
  // the redraft route leaves status 'ready', so the text change is the landing signal).
  const claimRef = useRef<string | null>(null);

  useEffect(() => {
    const sig = `${item.status}|${item.draft_text ?? ''}`;
    if (sig !== claimRef.current) setDrafting(false);
    if (item.draft_text === lastText.current) return;
    lastText.current = item.draft_text ?? null;
    if (item.status === 'ready' && item.draft_text && !dirty) setText(item.draft_text);
  }, [item.draft_text, item.status, dirty]);

  // Fallback clear: a redraft that regenerates BYTE-IDENTICAL text produces no
  // signature change (KIT has no per-draft timestamp), so the effect above would
  // never clear `drafting` and the operator would be locked out of Send/Redraft.
  // Bound the indicator so it always releases — the normal (text-changed) case
  // still clears promptly via the signature effect well before this fires.
  useEffect(() => {
    if (!drafting) return;
    const t = setTimeout(() => setDrafting(false), DRAFTING_MAX_MS);
    return () => clearTimeout(t);
  }, [drafting]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['keep-in-touch'] });
  const draftMut = useMutation({
    mutationFn: () => draftKeepInTouchSuggestion(item.id),
    onMutate: () => {
      setErr(null);
      setDirty(false);
      claimRef.current = `${item.status}|${item.draft_text ?? ''}`;
      setDrafting(true);
    },
    onSuccess: invalidate,
    onError: (e) => {
      setDrafting(false);
      setErr(serverErrorMessage(e, 'draft failed'));
    },
  });
  const skipMut = useMutation({
    mutationFn: () => skipKeepInTouchSuggestion(item.id),
    onSuccess: invalidate,
    onError: (e) => setErr(serverErrorMessage(e, 'skip failed')),
  });
  const snoozeMut = useMutation({
    mutationFn: (until: string | null) => snoozeKeepInTouchSuggestion(item.id, until),
    onSuccess: invalidate,
    onError: (e) => setErr(serverErrorMessage(e, 'snooze failed')),
  });
  const sendMut = useMutation({
    mutationFn: () => dispatchKeepInTouchSuggestion(item.id, text.trim() || undefined),
    onMutate: () => setErr(null),
    onSuccess: () => {
      setSent(true);
      void invalidate();
    },
    onError: (e) => setErr(serverErrorMessage(e, 'send failed')),
  });

  const snoozed = isFutureSnooze(item);

  return (
    <div className="jc-canvas" data-testid="kit-canvas">
      <div className="jc-canvas-head">
        <h2>{item.contact_name ?? 'A contact'}</h2>
        <span className="jc-kind">{item.trigger}</span>
      </div>

      {item.status === 'ready' ? (
        <>
          <AiEvidenceStrip confidence={item.confidence} factsUsed={item.facts_used} />
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(true);
            }}
            rows={4}
            className="jc-draft"
            aria-label="Keep-in-touch draft"
          />
          <p className="jc-hint">
            {sendArmed
              ? 'Edit if needed, then Send — it replies in your existing conversation.'
              : 'Review only — sending is not enabled.'}
          </p>
        </>
      ) : item.status === 'suppressed' ? (
        <p className="jc-suppressed">AI declined: {item.reason ?? 'no appropriate message'}</p>
      ) : item.status === 'failed' ? (
        <p className="jc-failed">Draft failed: {item.reason ?? 'error'}</p>
      ) : drafting ? (
        <p className="jc-muted">Drafting…</p>
      ) : (
        <p className="jc-muted">No draft yet.</p>
      )}
      {err ? <p className="jc-failed">{err}</p> : null}

      <div className="jc-actions">
        <button
          type="button"
          className="jc-btn"
          onClick={() => draftMut.mutate()}
          disabled={draftMut.isPending || drafting}
        >
          {drafting
            ? 'Drafting…'
            : item.status === 'ready' || item.status === 'suppressed' || item.status === 'failed'
              ? '✨ Redraft'
              : '✨ AI draft'}
        </button>
        {sent || item.status === 'sent' ? (
          <span className="jc-sent" data-testid="kit-sent">
            Sent ✓
          </span>
        ) : sendArmed && item.status === 'ready' && !drafting ? (
          <button
            type="button"
            className="jc-btn jc-send"
            data-testid="kit-send"
            onClick={() => sendMut.mutate()}
            disabled={sendMut.isPending || !text.trim()}
          >
            {sendMut.isPending ? 'Sending…' : 'Send'}
          </button>
        ) : null}
        <button
          type="button"
          className="jc-btn"
          onClick={() => skipMut.mutate()}
          disabled={skipMut.isPending}
        >
          Skip
        </button>
      </div>

      <div className="kit-snooze">
        <SnoozePopover
          onPick={(until) => snoozeMut.mutate(until)}
          onUnsnooze={() => snoozeMut.mutate(null)}
          isSnoozed={snoozed}
          disabled={snoozeMut.isPending}
        />
        {!snoozed ? (
          <button
            type="button"
            className="jc-btn"
            data-testid="kit-snooze-2w"
            onClick={() => snoozeMut.mutate(snoozeUntilIso(24 * 14))}
            disabled={snoozeMut.isPending}
          >
            Snooze 2 weeks
          </button>
        ) : null}
      </div>
    </div>
  );
}
